<script setup>
import { ref } from 'vue';
import { useRoute } from 'vue-router';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['import']);
const route = useRoute();

const dialogRef = ref(null);
const fileInput = ref(null);
const hasSelectedFile = ref(null);
const selectedFileName = ref('');
const isImporting = ref(false);

const downloadSample = async () => {
  const accountId = route.params.accountId;
  const res = await window.axios.get(
    `/api/v1/accounts/${accountId}/companies/import_sample`,
    { responseType: 'blob' }
  );
  const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', 'import-companies-sample.csv');
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const processFileName = fileName => {
  const lastDotIndex = fileName.lastIndexOf('.');
  const extension = fileName.slice(lastDotIndex);
  const baseName = fileName.slice(0, lastDotIndex);
  return baseName.length > 20 ? `${baseName.slice(0, 20)}...${extension}` : fileName;
};

const handleFileClick = () => fileInput.value?.click();

const handleFileChange = () => {
  const file = fileInput.value?.files[0];
  hasSelectedFile.value = file || null;
  selectedFileName.value = file ? processFileName(file.name) : '';
};

const handleRemoveFile = () => {
  hasSelectedFile.value = null;
  selectedFileName.value = '';
  if (fileInput.value) fileInput.value.value = null;
};

const uploadFile = async () => {
  if (!hasSelectedFile.value) return;
  isImporting.value = true;
  try {
    emit('import', hasSelectedFile.value);
    handleRemoveFile();
    dialogRef.value?.close();
  } finally {
    isImporting.value = false;
  }
};

const open = () => dialogRef.value?.open();

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    title="Import companies"
    confirm-button-label="Import"
    cancel-button-label="Cancel"
    :is-loading="isImporting"
    :disable-confirm-button="!hasSelectedFile || isImporting"
    @confirm="uploadFile"
  >
    <template #description>
      <p class="mb-0 text-sm text-n-slate-11">
        Import companies through a CSV file.
        <a href="#" class="text-n-blue-11" @click.prevent="downloadSample">
          Download a sample csv.
        </a>
      </p>
    </template>

    <div class="flex flex-col gap-2">
      <div class="flex items-center gap-2">
        <label class="text-sm text-n-slate-12 whitespace-nowrap">CSV File:</label>
        <div class="flex items-center justify-between w-full gap-2">
          <span v-if="hasSelectedFile" class="text-sm text-n-slate-12">
            {{ selectedFileName }}
          </span>
          <Button
            v-if="!hasSelectedFile"
            label="Choose file"
            icon="i-lucide-upload"
            color="slate"
            variant="ghost"
            size="sm"
            class="!w-fit"
            @click="handleFileClick"
          />
          <div v-else class="flex items-center gap-1">
            <Button
              label="Change"
              color="slate"
              variant="ghost"
              size="sm"
              @click="handleFileClick"
            />
            <div class="w-px h-3 bg-n-strong" />
            <Button
              icon="i-lucide-trash"
              color="slate"
              variant="ghost"
              size="sm"
              @click="handleRemoveFile"
            />
          </div>
        </div>
      </div>
    </div>

    <input
      ref="fileInput"
      type="file"
      accept="text/csv"
      class="hidden"
      @change="handleFileChange"
    />
  </Dialog>
</template>
